import { useState, useEffect } from 'react';

import { getAGGClient } from '@confluence/graphql';
import { useSessionData } from '@confluence/session-data';

import { LoomVideoDurationsQuery } from '../LoomVideoDurationsQuery.agggraphql';
import type { LoomVideoDurationsQuery as LoomVideoDurationsQueryType } from '../__types__/LoomVideoDurationsQuery';

export const getLoomARI = ({
	cloudId,
	activationId,
	videoId,
}: {
	cloudId: string;
	activationId: string;
	videoId: string;
}) => `ari:cloud:loom:${cloudId}:video/activation/${activationId}/${videoId}`;

export const useLoadVideoDurations = (videos: { id: string; url: string }[]) => {
	const [videoDurations, setVideoDurations] = useState(0);
	const [isLoadingDurations, setIsLoadingDurations] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const { cloudId, activationId } = useSessionData();

	useEffect(() => {
		let isMounted = true;

		const fetchAllDurations = async () => {
			if (!videos.length) {
				setIsLoadingDurations(false);
				return;
			}

			setIsLoadingDurations(true);
			setError(null);

			try {
				const client = getAGGClient();
				let totalDuration = 0;

				for (const video of videos) {
					if (!isMounted) break;

					const response = await client.query<LoomVideoDurationsQueryType>({
						query: LoomVideoDurationsQuery,
						variables: {
							id: getLoomARI({
								cloudId,
								activationId,
								videoId: video.id,
							}),
						},
					});

					if (!isMounted) break;

					if (response.data?.loom_videoDurations?.playableDuration) {
						totalDuration += response.data.loom_videoDurations.playableDuration;
					}
				}

				if (isMounted) {
					setVideoDurations(totalDuration * 1000);
					setIsLoadingDurations(false);
				}
			} catch (err) {
				if (isMounted) {
					setError(err instanceof Error ? err : new Error('Unknown error'));
					setIsLoadingDurations(false);
				}
			}
		};

		void fetchAllDurations();

		return () => {
			isMounted = false;
		};
	}, [videos, cloudId, activationId]);

	return {
		videoDurations,
		isLoadingDurations,
		error,
	};
};
